import { Component, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-projects-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1700px] mx-auto p-5">
        <div class="flex items-center justify-between mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">{{ stageTitle() }}</h1>
            <p class="text-slate-500 text-xs">{{ stageDesc() }}</p>
          </div>
          <div class="flex items-center gap-2">
            <div class="flex gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
              <a routerLink="/admin/projects" [queryParams]="{ status: 'active' }" class="h-8 px-3 rounded-md text-xs font-semibold transition-colors flex items-center"
                [class.bg-white]="!isCompletedView()" [class.text-[#1A6FA6]]="!isCompletedView()" [class.shadow-sm]="!isCompletedView()"
                [class.text-slate-500]="isCompletedView()">Active</a>
              <a routerLink="/admin/projects" [queryParams]="{ status: 'completed' }" class="h-8 px-3 rounded-md text-xs font-semibold transition-colors flex items-center"
                [class.bg-white]="isCompletedView()" [class.text-[#1A6FA6]]="isCompletedView()" [class.shadow-sm]="isCompletedView()"
                [class.text-slate-500]="!isCompletedView()">Completed</a>
            </div>
            @if (!isCompletedView()) {
              <button (click)="openCreateModal()" class="px-3 py-2 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 cursor-pointer flex items-center gap-2" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                <i class="ti ti-plus text-[16px]"></i> New Project
              </button>
            }
          </div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-5 gap-2.5 mb-4">
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total Projects</p>
              <p class="text-lg font-semibold text-slate-900 mt-0.5">{{ projects().length }}</p>
            </div>
            <span class="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 text-slate-500 flex items-center justify-center"><i class="ti ti-building-skyscraper"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">In Progress</p>
              <p class="text-lg font-semibold text-amber-700 mt-0.5">{{ countByStatus('In Progress') }}</p>
            </div>
            <span class="w-8 h-8 rounded-lg bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center"><i class="ti ti-progress"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Completed</p>
              <p class="text-lg font-semibold text-emerald-700 mt-0.5">{{ countByStatus('Completed') }}</p>
            </div>
            <span class="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center"><i class="ti ti-circle-check"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Needs Review</p>
              <p class="text-lg font-semibold text-[#1A6FA6] mt-0.5">{{ totalReviewPhases() }}</p>
            </div>
            <span class="w-8 h-8 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-file-check"></i></span>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm px-3.5 py-2.5 flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Open Phases</p>
              <p class="text-lg font-semibold text-[#1A6FA6] mt-0.5">{{ totalOpenPhases() }}</p>
            </div>
            <span class="w-8 h-8 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-checklist"></i></span>
          </div>
        </div>

        @if (!isCompletedView()) {
          <div class="mb-4">
            <div class="inline-flex max-w-full gap-1 bg-white border border-[#E7EEF5] rounded-xl p-1 overflow-x-auto shadow-sm">
              @for (filter of quickFilters; track filter.key) {
                <button type="button" (click)="setQuickFilter(filter.key)"
                  class="px-3 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-2"
                  [class.bg-[#EAF5FA]]="quickFilter() === filter.key"
                  [class.text-[#1A6FA6]]="quickFilter() === filter.key"
                  [class.text-slate-500]="quickFilter() !== filter.key">
                  <i class="ti text-[15px]" [class]="filter.icon"></i>
                  {{ filter.label }} <span class="opacity-70">({{ quickFilterCount(filter.key) }})</span>
                </button>
              }
            </div>
          </div>
        }

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">

          <!-- Skeleton loading -->
          @if (isLoading()) {
            @for (n of skeletons; track n) {
              <div class="bg-white rounded-xl p-4 border border-[#E7EEF5] shadow-sm animate-pulse">
                <div class="flex justify-between mb-3">
                  <div class="h-4 w-24 bg-slate-100 rounded-md"></div>
                  <div class="h-4 w-14 bg-slate-100 rounded-md"></div>
                </div>
                <div class="h-5 w-3/4 bg-slate-200 rounded-md mb-3"></div>
                <div class="grid grid-cols-2 gap-2 mb-3">
                  <div class="h-10 bg-slate-50 rounded-lg"></div>
                  <div class="h-10 bg-slate-50 rounded-lg"></div>
                </div>
                <div class="h-8 bg-slate-100 rounded-lg"></div>
              </div>
            }

          <!-- Empty state -->
          } @else if (filteredProjects().length === 0) {
            <div class="col-span-full bg-white rounded-xl p-12 flex flex-col items-center justify-center text-center border border-dashed border-slate-300 shadow-sm">
              <div class="w-16 h-16 rounded-lg flex items-center justify-center mb-4 bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6]">
                <i class="ti ti-building-skyscraper text-3xl"></i>
              </div>
              @if (isCompletedView()) {
                <h3 class="text-lg font-semibold text-slate-900 mb-2">No completed projects</h3>
                <p class="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">Completed project records will appear here once the second fix is approved and the project is closed.</p>
              } @else {
                <h3 class="text-lg font-semibold text-slate-900 mb-2">No active projects</h3>
                <p class="text-slate-500 text-sm max-w-md mx-auto mb-6 leading-relaxed">Create a project to track phases, documents, forms, workers, and quotes from one place.</p>
                <button (click)="openCreateModal()" class="px-5 py-2.5 bg-[#1A6FA6] text-white rounded-lg text-sm font-semibold shadow-sm hover:bg-[#0C3A5A] flex items-center gap-2 cursor-pointer transition-colors">
                  <i class="ti ti-plus text-base"></i> Create Project
                </button>
              }
            </div>

          <!-- Project cards -->
          } @else {
            @for (project of filteredProjects(); track project.id) {
              <a [routerLink]="['/admin/projects', project.id]" class="bg-white rounded-xl p-3 hover:shadow-md border border-[#E7EEF5] shadow-sm flex flex-col text-left group cursor-pointer transition-all hover:-translate-y-0.5 hover:border-[#BEE7F9]">
                <div class="flex items-start gap-3 mb-2.5 w-full">
                  <span class="w-9 h-9 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6] flex items-center justify-center text-sm font-semibold shrink-0">{{ projectInitial(project) }}</span>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-end gap-2 min-w-0">
                      @if (project.status) {
                        <span class="px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase border whitespace-nowrap shrink-0" [ngClass]="statusBadgeClass(project.status)">
                          {{project.status}}
                        </span>
                      }
                    </div>
                    <h3 class="text-base font-semibold text-slate-950 group-hover:text-[#1A6FA6] truncate mt-1">{{project.name}}</h3>
                  </div>
                </div>

                <div class="space-y-1.5 mb-2.5">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{{project.id}}</p>
                  @if (project.customer) {
                    <p class="text-xs text-slate-500 flex items-center gap-2 min-w-0">
                      <i class="ti ti-user-circle text-slate-400 shrink-0"></i>
                      <span class="truncate">{{project.customer}}</span>
                    </p>
                  }
                  @if (project.address) {
                    <p class="text-xs text-slate-500 flex items-center gap-2 min-w-0">
                      <i class="ti ti-map-pin text-slate-400 shrink-0"></i>
                      <span class="truncate">{{project.address}}</span>
                    </p>
                  }
                </div>

                @if (project.description) {
                  <p class="text-xs text-slate-600 leading-relaxed truncate mb-2.5">{{project.description}}</p>
                }

                <div class="grid grid-cols-2 gap-2 mb-2.5 mt-auto">
                  <div class="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2">
                    <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Phases</p>
                    <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ project.open_phases }} open</p>
                  </div>
                  <div class="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2">
                    <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Team</p>
                    <p class="text-sm font-semibold text-slate-900 mt-0.5">{{ project.assigned_worker_count }} worker(s)</p>
                  </div>
                </div>

                @if (project.estimated_end_date) {
                  <div class="rounded-lg bg-sky-50/70 border border-sky-100 px-3 py-2 mb-2.5">
                    <p class="text-[10px] uppercase tracking-widest font-bold text-sky-500">Estimated Completion</p>
                    <p class="text-sm font-semibold text-sky-900 mt-0.5">{{ project.estimated_end_date | date:'d MMM y' }}</p>
                  </div>
                }

                <div class="flex flex-wrap gap-1.5 mb-2.5">
                  <span class="px-2 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="project.declaration_status === 'Signed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'">
                    {{ project.declaration_status === 'Signed' ? 'Declaration signed' : 'Declaration pending' }}
                  </span>
                  @if (project.submitted_phase_count > 0) {
                    <span class="px-2 py-1 rounded-md text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-100">{{ project.submitted_phase_count }} review</span>
                  }
                  @if (project.needs_changes_phase_count > 0) {
                    <span class="px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100">{{ project.needs_changes_phase_count }} changes</span>
                  }
                  @if (project.document_count > 0) {
                    <span class="px-2 py-1 rounded-md text-[10px] font-semibold bg-slate-50 text-slate-600 border border-slate-100">{{ project.document_count }} docs</span>
                  }
                </div>

                <div class="flex items-center justify-between pt-2.5 border-t border-slate-100 w-full">
                  <span class="text-xs font-semibold text-slate-500">Updated {{ project.updated_at ? (project.updated_at | date:'d MMM') : '-' }}</span>
                  <span class="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 group-hover:bg-[#1A6FA6] group-hover:text-white group-hover:border-[#1A6FA6] transition-colors">
                    <i class="ti ti-arrow-right"></i>
                  </span>
                </div>
              </a>
            }
          }
        </div>

        <!-- Create Project Modal (Themed) -->
        @if (showCreateModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-[#E7EEF5]">
              <div class="flex items-center justify-between p-5 border-b border-slate-100 bg-white">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Create Project</h2>
                  <p class="text-xs text-slate-500 mt-1">Set the site and customer details before moving into phase tracking.</p>
                </div>
                <button (click)="closeCreateModal()" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 transition-colors cursor-pointer"><i class="ti ti-x text-lg"></i></button>
              </div>
              <form #projForm="ngForm" (ngSubmit)="onSubmit(projForm)" class="flex flex-col flex-1 overflow-hidden">
                <div class="p-5 overflow-y-auto flex-1 space-y-5">
                  <div class="space-y-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <h3 class="text-xs font-bold text-slate-400 uppercase tracking-widest">Site Details</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div class="col-span-1 md:col-span-2">
                        <label class="block text-xs font-semibold text-slate-600 mb-1.5">Project Name</label>
                        <input type="text" name="name" [(ngModel)]="projectName" required class="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-all text-sm text-slate-800" placeholder="e.g. 50 Unit Development - Hobsonville">
                      </div>
                    
                      <div class="col-span-1 md:col-span-2 relative">
                        <label class="block text-xs font-semibold text-slate-600 mb-1.5">Phone Number <span class="text-slate-400 font-normal ml-1">(Search to autofill)</span></label>
                        <div class="flex items-center h-10 rounded-lg border border-slate-200 bg-slate-50 focus-within:border-[#1A6FA6] focus-within:bg-white transition-colors">
                          <div class="px-3 text-slate-500 font-semibold border-r border-slate-200 h-full flex items-center">+64</div>
                          <input type="text" name="phone" [(ngModel)]="searchPhone" (ngModelChange)="onPhoneChange($event)" class="w-full h-full px-3 outline-none text-sm text-slate-800 bg-transparent" placeholder="21 123 4567" autocomplete="off">
                        </div>
                      
                        @if (searchResults().length > 0) {
                          <div class="absolute w-full mt-2 bg-white rounded-xl shadow-lg border border-slate-100 z-10 overflow-hidden">
                            @for (customer of searchResults(); track customer.phone) {
                              <div (click)="selectCustomer(customer)" class="p-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 cursor-pointer transition-colors">
                                <p class="text-sm font-semibold text-slate-800">{{customer.name}}</p>
                                <p class="text-xs text-slate-500 flex items-center gap-2 mt-0.5"><i class="ti ti-phone"></i> {{customer.phone}} <span *ngIf="customer.email" class="ml-2"><i class="ti ti-mail"></i> {{customer.email}}</span></p>
                              </div>
                            }
                          </div>
                        }
                      </div>

                      <div>
                        <label class="block text-xs font-semibold text-slate-600 mb-1.5">Developer / Client Name</label>
                        <input type="text" name="customer" [(ngModel)]="customerName" required class="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-all text-sm text-slate-800">
                      </div>
                      <div>
                        <label class="block text-xs font-semibold text-slate-600 mb-1.5">Email</label>
                        <input type="email" name="email" [(ngModel)]="customerEmail" required class="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-all text-sm text-slate-800">
                      </div>
                      <div class="col-span-1 md:col-span-2">
                        <label class="block text-xs font-semibold text-slate-600 mb-1.5">Site Address</label>
                        <input #siteAddressInput type="text" name="address" [(ngModel)]="customerAddress" (ngModelChange)="onAddressManualChange()" required class="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-all text-sm text-slate-800">
                        @if (mapsKey()) {
                          <p class="text-[10px] font-semibold text-slate-400 mt-1">Select an Auckland Google Maps address when available, or type the site address manually.</p>
                        }
                      </div>
                      <div class="col-span-1 md:col-span-2">
                        <label class="block text-xs font-semibold text-slate-600 mb-1.5">Estimated Completion Date</label>
                        <input type="date" name="estimated_end_date" [(ngModel)]="estimatedEndDate" class="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-all text-sm text-slate-800">
                      </div>
                    </div>
                  </div>
                  <div class="space-y-3 pt-4 border-t border-slate-100">
                    <label class="block text-xs font-semibold text-slate-600">Scope / Description</label>
                    <textarea name="desc" ngModel required rows="4" class="w-full p-3 rounded-lg border border-slate-200 bg-slate-50 outline-none focus:bg-white focus:border-[#1A6FA6] transition-all text-sm text-slate-800 resize-none"></textarea>
                  </div>
                </div>
                <div class="p-5 border-t border-slate-100 bg-slate-50/70 flex justify-end gap-2">
                  <button type="button" (click)="closeCreateModal()" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200 transition-colors cursor-pointer">Cancel</button>
                  <button type="submit" [disabled]="!projForm.valid" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-[#0C3A5A] disabled:opacity-50 transition-colors cursor-pointer">Create Project</button>
                </div>
              </form>
            </div>
          </div>
        }
      </div>
    </div>
  `
})
export class AdminProjectsListComponent implements OnInit {
  @ViewChild('siteAddressInput') siteAddressInput?: ElementRef<HTMLInputElement>;

  projects = signal<any[]>([]);
  quickFilter = signal('all');
  quickFilters = [
    { key: 'all', label: 'All', icon: 'ti-list-details' },
    { key: 'review', label: 'Needs Review', icon: 'ti-file-check' },
    { key: 'changes', label: 'Needs Changes', icon: 'ti-message-report' },
    { key: 'declaration', label: 'Declaration Pending', icon: 'ti-file-alert' },
    { key: 'balance', label: 'Balance Due', icon: 'ti-currency-dollar' }
  ];
  showCreateModal = signal(false);
  stageTitle = signal('Development Projects');
  stageDesc = signal('Manage long-term sites and multi-phase developments');
  isLoading = signal(true);
  isCompletedView = signal(false);
  skeletons = [1, 2, 3, 4, 5, 6];

  projectName = '';
  searchPhone = '';
  selectedCustomerId: number | null = null;
  customerName = '';
  customerEmail = '';
  customerAddress = '';
  addressPlaceId = '';
  addressLatitude: number | null = null;
  addressLongitude: number | null = null;
  mapsKey = signal('');
  mapsLoaded = signal(false);
  estimatedEndDate = '';
  searchResults = signal<any[]>([]);
  searchTimeout: any;

  private api = inject(ApiService);
  private route = inject(ActivatedRoute);

  ngOnInit() {
    this.loadMapSettings();
    // Listen to both route params (legacy stage filter) AND query params (new status filter)
    this.route.queryParamMap.subscribe(queryParams => {
      const status = queryParams.get('status');
      if (status === 'active') {
        this.isCompletedView.set(false);
        this.stageTitle.set('Active Projects');
        this.stageDesc.set('All currently active development projects');
        this.loadProjectsByStatus(null);
      } else if (status === 'completed') {
        this.isCompletedView.set(true);
        this.stageTitle.set('Completed Projects');
        this.stageDesc.set('All successfully completed development projects');
        this.loadProjectsByStatus(['Completed']);
      } else {
        this.isCompletedView.set(false);
        // Fall back to stage-based param for backward compat
        this.route.paramMap.subscribe(params => {
          const stageId = params.get('stageId');
          if (stageId === 'first-fix') {
            this.stageTitle.set('Stage: First Fix (Pre-wire)');
            this.stageDesc.set('Projects currently in the initial plumbing layout phase');
            this.loadProjects('First Fix');
          } else if (stageId === 'rough-in') {
            this.stageTitle.set('Stage: Rough-in');
            this.stageDesc.set('Projects currently in the framing and pipework phase');
            this.loadProjects('Rough-in');
          } else if (stageId === 'second-fix') {
            this.stageTitle.set('Stage: Second Fix (Fit-out)');
            this.stageDesc.set('Projects in the final fixtures and finishing phase');
            this.loadProjects('Second Fix');
          } else {
            this.stageTitle.set('Development Projects');
            this.stageDesc.set('Manage long-term sites and multi-phase developments');
            this.loadProjects();
          }
        });
      }
    });
  }

  loadProjectsByStatus(statuses: string[] | null) {
    this.isLoading.set(true);
    this.api.getProjects().subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.projects) {
          this.projects.set(
            res.data.projects
              .filter((p: any) => statuses === null ? p.status !== 'Completed' : statuses.includes(p.status))
              .map((p: any) => this.mapProject(p))
          );
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  loadProjects(statusFilter?: string) {
    this.isLoading.set(true);
    this.api.getProjects(statusFilter).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success && res.data?.projects) {
          this.projects.set(res.data.projects.map((p: any) => this.mapProject(p)));
        }
      },
      error: () => this.isLoading.set(false)
    });
  }

  openCreateModal() {
    this.showCreateModal.set(true);
    setTimeout(() => this.initAddressAutocomplete(), 0);
  }
  closeCreateModal() { this.showCreateModal.set(false); }

  countByStatus(status: string): number {
    return this.projects().filter(project => project.status === status).length;
  }

  setQuickFilter(filter: string) {
    this.quickFilter.set(filter);
  }

  filteredProjects(): any[] {
    const filter = this.quickFilter();
    if (filter === 'review') return this.projects().filter(project => project.submitted_phase_count > 0);
    if (filter === 'changes') return this.projects().filter(project => project.needs_changes_phase_count > 0);
    if (filter === 'declaration') return this.projects().filter(project => project.declaration_status !== 'Signed');
    if (filter === 'balance') return this.projects().filter(project => project.balance_due_total > 0);
    return this.projects();
  }

  quickFilterCount(filter: string): number {
    if (filter === 'review') return this.projects().filter(project => project.submitted_phase_count > 0).length;
    if (filter === 'changes') return this.projects().filter(project => project.needs_changes_phase_count > 0).length;
    if (filter === 'declaration') return this.projects().filter(project => project.declaration_status !== 'Signed').length;
    if (filter === 'balance') return this.projects().filter(project => project.balance_due_total > 0).length;
    return this.projects().length;
  }

  totalOpenPhases(): number {
    return this.projects().reduce((sum, project) => sum + Number(project.open_phases || 0), 0);
  }

  totalReviewPhases(): number {
    return this.projects().reduce((sum, project) => sum + Number(project.submitted_phase_count || 0), 0);
  }

  projectInitial(project: any): string {
    return String(project?.name || project?.customer || 'P').trim().charAt(0).toUpperCase();
  }

  statusBadgeClass(status: string): string {
    if (status === 'Completed') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'In Progress') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (status === 'Planning') return 'bg-sky-50 text-sky-700 border-sky-200';
    if (status === 'Ready') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (status === 'On Hold') return 'bg-rose-50 text-rose-700 border-rose-200';
    return 'bg-slate-50 text-slate-600 border-slate-200';
  }

  mapProject(project: any): any {
    const customer = project.customer_name
      || project.contacts?.find((contact: any) => contact.type === 'Customer' || contact.contact_type === 'Customer')?.name
      || '';

    return {
      id: project.id,
      name: project.name || `#${project.id}`,
      status: project.status || '',
      customer,
      address: project.address || '',
      description: project.description || '',
      open_phases: this.openPhaseCount(project),
      submitted_phase_count: Number(project.submitted_phase_count || 0),
      needs_changes_phase_count: Number(project.needs_changes_phase_count || 0),
      assigned_worker_count: Number(project.assigned_worker_count || 0),
      declaration_status: project.declaration_status || 'Unsigned',
      balance_due_total: Number(project.balance_due_total || 0),
      document_count: Number(project.uploaded_document_count || 0)
        + Number(project.as_built_count || 0)
        + Number(project.insurance_report_count || 0)
        + Number(project.quote_count || 0)
        + Number(project.invoice_count || 0),
      updated_at: project.updated_at || project.created_at || '',
      estimated_end_date: project.estimated_end_date || ''
    };
  }

  openPhaseCount(project: any): number {
    if (project.status === 'Completed') return 0;
    const value = project.active_phases ?? project.open_phases ?? project.open_phase_count ?? 0;
    return Number(value) || 0;
  }

  onPhoneChange(value: string) {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (value.length >= 5) {
      this.searchTimeout = setTimeout(() => {
        let query = value.startsWith('0') ? value.substring(1) : value;
        query = query.replace(/\s+/g, '');
        
        this.api.searchCustomerByPhone('+64' + query).subscribe({
          next: (res: any) => {
            if (res.success && res.data?.customers) {
              this.searchResults.set(res.data.customers);
            }
          }
        });
      }, 500);
    } else {
      this.searchResults.set([]);
    }
  }

  selectCustomer(customer: any) {
    this.selectedCustomerId = customer.id ? Number(customer.id) : null;
    this.customerName = customer.name;
    this.customerEmail = customer.email || '';
    this.customerAddress = customer.address || this.customerAddress;
    this.addressPlaceId = customer.address_place_id || '';
    this.addressLatitude = customer.latitude !== undefined && customer.latitude !== null ? Number(customer.latitude) : null;
    this.addressLongitude = customer.longitude !== undefined && customer.longitude !== null ? Number(customer.longitude) : null;
    this.searchPhone = customer.phone.replace(/^\+64\s*/, '').trim();
    this.searchResults.set([]);
  }

  onAddressManualChange() {
    this.addressPlaceId = '';
    this.addressLatitude = null;
    this.addressLongitude = null;
  }

  onSubmit(form: NgForm) {
      if (!this.customerName || !this.customerAddress || !this.projectName || !this.customerEmail) {
          showToast('error', 'Project Title, Client Name, Client Email, and Address are strictly required.');
          return;
      }
      const payload = {
        name: this.projectName,
        description: form.value.desc,
        address: this.customerAddress,
        address_place_id: this.addressPlaceId,
        latitude: this.addressLatitude,
        longitude: this.addressLongitude,
        estimated_end_date: this.estimatedEndDate || null,
        customer_id: this.selectedCustomerId,
        customer_name: this.customerName,
        contacts: [
            { name: this.customerName, phone: '+64 ' + this.searchPhone.trim(), email: this.customerEmail, type: 'Customer' }
        ]
      };
      
      this.api.createProject(payload).subscribe({
        next: (res: any) => {
            if (res.success) {
                showToast('success', 'Project created successfully.');
                this.loadProjects();
                this.closeCreateModal();
                this.estimatedEndDate = '';
            } else {
                showToast('error', res.message || 'Failed to create project.');
            }
        },
        error: () => showToast('error', 'Network or server error occurred.')
      });
  }

  private loadMapSettings() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const key = String(res?.data?.google_maps_browser_api_key || '').trim();
        this.mapsKey.set(key);
        if (key) this.loadGoogleMapsScript(key);
      }
    });
  }

  private loadGoogleMapsScript(key: string) {
    if (typeof window === 'undefined') return;
    if ((window as any).google?.maps?.places) {
      this.mapsLoaded.set(true);
      this.initAddressAutocomplete();
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => {
        this.mapsLoaded.set(true);
        this.initAddressAutocomplete();
      });
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.mapsLoaded.set(true);
      this.initAddressAutocomplete();
    };
    document.head.appendChild(script);
  }

  private initAddressAutocomplete() {
    if (!this.siteAddressInput?.nativeElement || !(window as any).google?.maps?.places) return;
    const google = (window as any).google;
    const autocomplete = new google.maps.places.Autocomplete(this.siteAddressInput.nativeElement, {
      componentRestrictions: { country: 'nz' },
      bounds: this.aucklandBounds(),
      strictBounds: true,
      fields: ['formatted_address', 'geometry', 'place_id']
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const lat = place.geometry?.location ? Number(place.geometry.location.lat()) : null;
      const lng = place.geometry?.location ? Number(place.geometry.location.lng()) : null;
      if (!this.isInAucklandBounds(lat, lng)) {
        this.addressPlaceId = '';
        this.addressLatitude = null;
        this.addressLongitude = null;
        showToast('error', 'Please select an Auckland address.');
        return;
      }
      this.customerAddress = place.formatted_address || this.customerAddress;
      this.addressPlaceId = place.place_id || '';
      this.addressLatitude = lat;
      this.addressLongitude = lng;
    });
  }

  private aucklandBounds() {
    return {
      north: -36.35,
      south: -37.35,
      east: 175.45,
      west: 174.15
    };
  }

  private isInAucklandBounds(lat: number | null, lng: number | null): boolean {
    if (lat === null || lng === null) return false;
    const bounds = this.aucklandBounds();
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
  }
}
